import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import sortBy from "lodash/sortBy";
import { useSnackbar } from "notistack";
import type { CreateOrUpdateProductOptionRequestBody, ProductOption } from "./productOptionsApi";
import {
  addProductOption,
  deleteProductOption,
  listProductOptions,
  updateProductOptionNameAndRoomConfig,
} from "./productOptionsApi";

const optionsKey = "product-options-dictionary";

export const useProductOptions = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: [optionsKey],
    queryFn: ({ signal }) => listProductOptions(signal),
  });

  const addMutation = useMutation<ProductOption, unknown, CreateOrUpdateProductOptionRequestBody>({
    mutationFn: (r) => addProductOption(r),
    onSuccess: async (data) => {
      enqueueSnackbar(`Opcja '${data.name}' została dodana do słownika`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [optionsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się dodać opcji do słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const updateNameAndRoomConfigMutation = useMutation<
    ProductOption,
    unknown,
    { id: string; request: CreateOrUpdateProductOptionRequestBody }
  >({
    mutationFn: ({ id, request }) => updateProductOptionNameAndRoomConfig(id, request),
    onSuccess: async (data) => {
      enqueueSnackbar(`Opcja '${data.name}' została zaktualizowana`, { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [optionsKey] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować opcji. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const deleteMutation = useMutation<void, unknown, { readonly id: string; readonly force: boolean }>({
    mutationFn: ({ id, force }) => deleteProductOption(id, force),
    onSuccess: async () => {
      enqueueSnackbar("Opcja została usunięta ze słownika", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: [optionsKey] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć opcji ze słownika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    productOptions: sortBy(data ?? [], ({ name }) => name.trim().toLowerCase()),
    isLoading,
    addProductOption: (request: CreateOrUpdateProductOptionRequestBody) => addMutation.mutateAsync(request),
    updateProductOptionNameAndRoomConfig: (optionId: string, request: CreateOrUpdateProductOptionRequestBody) =>
      updateNameAndRoomConfigMutation.mutateAsync({ id: optionId, request }),
    deleteProductOption: (id: string, force: boolean) => deleteMutation.mutateAsync({ id, force }),
  };
};
